/**
 * Downscale + re-encode a user-selected image to a JPEG suitable for OCR upload.
 * Camera photos are routinely 10–50 MB and exceed the server-side upload cap;
 * Textract also doesn't need full resolution. We cap the long edge and re-encode
 * to JPEG via a canvas, returning a Blob whose size is used for both the upload
 * ticket and the PUT.
 *
 * Falls back to the original file if the browser can't decode it (e.g. HEIC on
 * a browser without a decoder) so the flow degrades rather than breaks — the
 * server-side size cap still applies.
 */
export async function downscaleToJpeg(
  file: File,
  options: { maxEdge?: number; quality?: number } = {},
): Promise<Blob> {
  const maxEdge = options.maxEdge ?? 1600;
  const quality = options.quality ?? 0.7;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // Undecodable here; let the server enforce limits.
  }

  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    return blob ?? file;
  } finally {
    bitmap.close();
  }
}
